import { price, feesConfig } from './price.config';
import { vehicles } from './vehicle.config';
import { language } from './language.config';
import { pagination } from './pagination.config';
import { order } from './order';
import { design } from './design.config';
import { contact } from './contact.config';
import { route } from './route.config';
import { region } from './region.config';
import { agreement } from './agreement.config';
import { headers } from './headers.config';
import { links } from './links';
import { APP_REGISTRY, BUSINESS_REGISTRY, PRODUCT_REGISTRY } from './app-registry.config';
import { googleAnalytics } from './google-analytics.config';
import { posthogConfig } from '../posthog/posthog.config';
import { payment } from './payment.config';
import { date } from './date.config';
import { time } from './time.config';
import { superAdmin } from './super-admin.config';
import { anonymousUser } from './anonymous-user.config';
import { image } from './image.config';
import { invoice } from './invoice.config';
import { client } from './client.config';
import { SUBSCRIPTION_CONFIG } from './subscription.config';
import { LEAD_FOLLOW_UP_CONFIG, resolveLeadFollowUpDate } from './lead.config';
import { message } from './message.config';
import { REFERRAL_CONFIG } from './referral.config';
export { REFERRAL_CONFIG } from './referral.config';
export type { ClientRole, ClientRoleConfig } from './client.config';
export type { LangConfig } from './language.config';
export { getLanguageConfig } from './language.config';
export type {
  OrderAutoMessageRule,
  OrderMessageTrigger,
  OrderMessageRecipient,
  OrderMessageRequire,
} from './message.config';
export {
  orderAutoMessageRules,
  orderUsesHeroWaybillPdf,
} from './message.config';
export {
  LEAD_FOLLOW_UP_CONFIG,
  resolveLeadFollowUpDate,
  type LeadFollowUpConfig,
} from './lead.config';
export {
  SUBSCRIPTION_CONFIG,
  DEFAULT_SUBSCRIPTION_BUSINESS,
  DEFAULT_SUBSCRIPTION_AUDIENCE,
  defaultCurrencyForCountry,
  countryFromPhone,
  resolveGuestCurrency,
  isPlanOnBusiness,
  getPlansForBusiness,
  getDefaultPlanForBusiness,
  isContactSalesPlan,
  isRecommendedPlan,
  intervalSavingsPercent,
  committedQtyConfigForUnit,
  getPlanSubscriptionModel,
  isUsagePlan,
  usageOrderTiers,
  usageTierForQty,
  usagePricePerUnit,
  minUsagePurchaseQty,
  resolveUsagePlanUpgrade,
  usagePricingLadder,
  usageTaxConfig,
  usagePriceDisplayMode,
  salesConfig,
  planHasUnlimitedOrders,
  defaultUnlimitedPlanCode,
  truckCountIssue,
  type TrialPolicy,
  type FreeCreditsPolicy,
  type FreeWindowStart,
  type PricingOffer,
  type PricingFlow,
  type PricingMenuOptionConfig,
  type SalesConfig,
  type SalesPlanConfig,
  type PerOrderPlanConfig,
  type UnlimitedPlanConfig,
  type SeatPlansConfig,
  type UsagePricingStep,
  type UsageTierConfig,
  type UsageAudiencePricing,
  type UsagePriceDisplayMode,
  type SubscriptionInterval,
  type SubscriptionAudience,
  type EntitlementGatePoint,
  type AddonCadence,
  type PlanCode,
  type AddonCode,
} from './subscription.config';
export {
  APP_REGISTRY,
  BUSINESS_REGISTRY,
  PRODUCT_REGISTRY,
  appEntry,
  appBusiness,
  appSurface,
  appBrand,
  businessEntry,
  productEntry,
  isClientSettableApp,
  isAgentSurfaceApp,
  businessCreditUnit,
  creditSpendsOnIssuance,
  businessDisplayName,
  productDisplayNameForProduct,
  businessWebsite,
  businessLogoUrl,
  businessWhatsAppLink,
  getPlanBusiness,
  getPlanProduct,
  planUnit,
  unitPricing,
  productsForBusiness,
  productBusiness,
  productAudience,
  productForAudience,
  plansForProductUnit,
  buildPurchase,
  assertPurchaseValid,
  orderBusiness,
  type AppEntry,
  type Business,
  type BusinessEntry,
  type ClientSettableAppId,
  type ProductEntry,
  type UnitPricing,
} from './app-registry.config';
export {
  FUNNEL_CONFIG,
  FUNNEL_STEP_LABELS,
  LEAD_STATUS_ORDER,
  TERMINAL_LEAD_STATUSES,
  funnelFor,
  funnelStepList,
  funnelSteps,
  type FunnelDefinition,
  type FunnelId,
  type FunnelRule,
  type FunnelStepConfig,
} from './funnel.config';
export {
  deriveLeadStatus,
  matchesFunnelRule,
  newLeadFunnel,
  nextFunnelStep,
  resolveFunnelBusiness,
  unmetRequirements,
} from './funnel.utils';
export { getInvoiceStatusConfig } from './invoice.config';
export { buildAccountBlockedSupportMailto } from './contact.utils';
export type { ActionConfig } from './order/order.status.config';
export type { GoogleEvent } from './google-analytics.config';
export type { TimelineNameId } from './order';
export type { CityChangeWindow } from './order/order.location-change.config';
export const CONFIG = {
  /**
   * Self-serve account changes through the agent (`AccountOpsService`):
   * a user may change their own account type only this many days after
   * signup, and only while nothing has been traded or paid. Staff can
   * always change it from the admin panel.
   */
  accountOps: { selfServeWindowDays: 14 },
  price,
  vehicles,
  language,
  pagination,
  order,
  design,
  feesConfig,
  contact,
  route,
  region,
  agreement,
  links,
  headers,
  /** Every Hero app, keyed by the `HeroAppId` that is persisted on `doc.app`. */
  apps: APP_REGISTRY,
  /** Every commercial brand — display name, website, credit noun. */
  businesses: BUSINESS_REGISTRY,
  /** Every audience product under a business — units, plan codes. */
  products: PRODUCT_REGISTRY,
  /** @deprecated Use PostHog instead. */
  googleAnalytics,
  payment,
  date,
  /** time config */
  time,
  /** super admin config */
  superAdmin,
  /** anonymous user (public @Public() requests without a token) */
  anonymousUser,
  /** invoice config */
  invoice,
  /** image upload presets */
  image,
  /** PostHog config (feature flags, analytics, session replay) */
  posthog: posthogConfig,
  /** client config such as Roles and others */
  client,
  /** order / transactional messaging auto-rules (WhatsApp waybill, …) */
  message,
  /** subscription catalog (plans + addons + per-currency pricing + tax + dunning) */
  subscription: SUBSCRIPTION_CONFIG,
  /**
   * Lead follow-up scheduling policy — Saudi-hour cutoff + weekend
   * skip rules used to auto-stamp `Lead.nextFollowUpDate` on public
   * form submissions. See {@link resolveLeadFollowUpDate}.
   */
  lead: {
    followUp: LEAD_FOLLOW_UP_CONFIG,
    resolveFollowUpDate: resolveLeadFollowUpDate,
  },
  /** Referral program — hit dedupe, tracking TTL, attribution window, cookie lifetime. */
  referral: REFERRAL_CONFIG,
} as const;
